/**
 * How much room the page actually has, and what to give up when it runs out.
 *
 * Bootstrap's breakpoints read the viewport, and in this app the viewport is a
 * poor proxy for the page: a 1440 laptop with the rail expanded gives the page
 * 1136px, and a 2560 monitor gives it 2256px, yet both are `xxl` (>=1400) and get
 * identical rules. Opening a 380px chat panel takes the same 1440 laptop down to
 * 756px while the breakpoint does not move at all. So the shell computes widths
 * rather than asking a media query, and the pages inside it use container queries
 * against the width this produces.
 *
 * Kept pure and free of `window` so the decision can be tested directly. The shell
 * reads `window.innerWidth` and hands it in.
 */

export const LAYOUT = {
  /**
   * Below this the dense pages stop being operable: the model overview's two
   * columns fall under ~430px each and the tree page's review column under 300px,
   * which is where cards start overflowing their own chrome.
   */
  MAIN_MIN_PX: 900,
  /** Wide enough to read a model answer without a scrollbar on every paragraph. */
  CHAT_DEFAULT_PX: 380,
  CHAT_MIN_PX: 320,
  CHAT_MAX_PX: 560,
  /**
   * Folded: what the column keeps. Nothing, because what reopens the panel is
   * the Ask AI button in the model header, not anything in the column; reserving
   * a strip left a visibly empty gutter down the side of every page whenever the
   * chat was shut.
   */
  CHAT_RAIL_PX: 0,
  /**
   * The column's own padding around the card, both sides. The card is inset
   * rather than flush, which is what the floating version achieved with
   * `right: 16` and a 12px top and bottom gap.
   */
  PANEL_INSET_PX: 26,
  NAV_EXPANDED_PX: 256,
  NAV_COLLAPSED_PX: 72,
  /**
   * The gutter `StudioPage` puts on each side of the content, at every width.
   *
   * One value, deliberately. It used to drop to 16 at 1280px wide, and that moved
   * everything aligned to the content edge (Deploy, the page header) by 8px
   * between two windows, and again between this layout and the classic one, which
   * is always 24. Keeping it fixed costs a window at or under 1280 16px of content.
   */
  GUTTER_PX: 24,
  /** The top bar, roomy and compact. */
  TOPBAR_PX: 64,
  TOPBAR_COMPACT_PX: 56,
  /**
   * Where the top bar compacts, matching `rgip-sales-copilot`'s shell (RGI-2198)
   * so the two products tighten up at the same moment rather than at two numbers
   * nobody chose together. Height only: the gutter does not compact (see
   * `GUTTER_PX`).
   */
  COMPACT_HEIGHT_PX: 860,
} as const;

/** The top bar at a given viewport height. Vertical metric, height decides it. */
export function topbarFor(viewportHeight: number): number {
  return viewportHeight <= LAYOUT.COMPACT_HEIGHT_PX
    ? LAYOUT.TOPBAR_COMPACT_PX
    : LAYOUT.TOPBAR_PX;
}

export interface LayoutFitInput {
  /** `window.innerWidth`. */
  viewport: number;
  chatOpen: boolean;
  /** The user's current panel width, before any rail decision. */
  chatWidth: number;
  /**
   * Whether the panel is currently folded to its rail. Supplied rather than
   * decided here, because it latches: the shell sets it when the room runs out
   * and only the user clears it.
   *
   * Deciding it from width alone made the panel spring open again as the window
   * grew, which took 380px back off the content in one step. Widening a window
   * must never shrink the content, and an assistant that reopens itself is
   * intrusive besides. Protective collapse is welcome; automatic expansion is
   * not.
   */
  panelRailed?: boolean;
}

export interface LayoutFit {
  /**
   * Collapse the nav rail without persisting it. The caller must route this
   * through `setForcedCollapsed`, never `setCollapsed`, or it rewrites a
   * preference the user set by hand.
   */
  forceNavCollapsed: boolean;
  /**
   * There is no longer room for the panel at its chosen width. The shell latches
   * this into the rail state; it is a signal, not the rendered answer.
   */
  chatAsRail: boolean;
  /**
   * What the content column ends up with, after both decisions.
   *
   * The shell does not paint from this: the grid derives the same width from the
   * column definition. It is here because it is the quantity the layout's one
   * invariant is stated over, that a wider window never yields less content, and
   * a number that is asserted every build is worth more than one that is
   * inferred from three others at review time.
   */
  mainWidth: number;
  /** The gutter the shell must paint, so the page cannot use a different one. */
  gutter: number;
}

function mainWidthFor(
  viewport: number,
  navWidth: number,
  chatCost: number
): number {
  // The gutter is derived here rather than declared twice. The shell publishes
  // this same number as `--shell-gutter` and the page reads it from there, so
  // the width this math assumes and the width actually painted cannot drift.
  return viewport - navWidth - LAYOUT.GUTTER_PX * 2 - chatCost;
}

/**
 * Decide what gives when the window is too narrow for everything at once.
 *
 * The nav rail goes first and the chat panel second. That order is deliberate:
 * the rail is navigation the user is not currently using, costs 184px, and folds
 * to something still clickable, whereas folding the panel takes away the thing
 * they just deliberately opened. On a 1440 laptop with a 380px panel, collapsing
 * the rail is the whole difference between 756px of content and 940px.
 */
export function resolveLayoutFit(input: LayoutFitInput): LayoutFit {
  const { viewport, chatOpen } = input;

  // With no panel open the rail is the user's business. It is only forced shut
  // when the window alone cannot hold the content, which is rare.
  if (!chatOpen) {
    const expanded = mainWidthFor(viewport, LAYOUT.NAV_EXPANDED_PX, 0);
    if (expanded >= LAYOUT.MAIN_MIN_PX) {
      return {
        forceNavCollapsed: false,
        chatAsRail: false,
        mainWidth: expanded,
        gutter: LAYOUT.GUTTER_PX,
      };
    }
    const collapsed = mainWidthFor(viewport, LAYOUT.NAV_COLLAPSED_PX, 0);
    return {
      forceNavCollapsed: true,
      chatAsRail: false,
      mainWidth: collapsed,
      gutter: LAYOUT.GUTTER_PX,
    };
  }

  // Panel open: the rail always yields to it, at every width.
  //
  // This was weighed against the floor first, and that produced a layout that
  // got WORSE as the window grew. Just under the boundary the rail was collapsed
  // and the content had about 1084px; one pixel over, the rail sprang back and
  // the content dropped to 900. Dragging a window wider made the content
  // narrower, which is indefensible however well the arithmetic reads.
  //
  // So the rail is not a measurement any more, it is a rule: the panel is the
  // thing the user just opened, the rail is navigation they are not using, and
  // it costs 184px to keep. One rule, one direction, nothing to predict.
  const cost = input.panelRailed ? LAYOUT.CHAT_RAIL_PX : input.chatWidth;
  const main = mainWidthFor(viewport, LAYOUT.NAV_COLLAPSED_PX, cost);

  // Whether the WINDOW can hold a panel at all, asked at the minimum width rather
  // than at the width the user chose.
  //
  // Keyed on the chosen width, this made the drag handle a trap: widen the panel
  // past the content floor and the shell folded the whole chat away, on release
  // rather than during the gesture, so the control destroyed the thing it belonged
  // to. Clamping the width instead was tried twice and is worse, as the note on
  // `onDragChange` in ShellPanel records: the ceiling lands at or below the current
  // width on an ordinary laptop, so the panel cannot be widened at all.
  //
  // Dragging the panel wider is an explicit act, and the floor exists to stop the
  // layout degrading on its own, not to overrule someone who asked for a wide panel.
  // So a drag now squeezes the content and never folds. Only a window too narrow for
  // even CHAT_MIN_PX folds, which is a fact about the window and cannot be caused by
  // touching the handle.
  const roomForPanel =
    mainWidthFor(
      viewport,
      LAYOUT.NAV_COLLAPSED_PX,
      LAYOUT.CHAT_MIN_PX + LAYOUT.PANEL_INSET_PX
    ) >= LAYOUT.MAIN_MIN_PX;

  return {
    forceNavCollapsed: true,
    chatAsRail: !roomForPanel,
    mainWidth: main,
    gutter: LAYOUT.GUTTER_PX,
  };
}

/**
 * The widest the panel may be dragged on this viewport.
 *
 * `CHAT_MAX_PX` is a flat number and the only thing stopping the panel on a large
 * screen, where the real constraint is already stated elsewhere: content must keep
 * `MAIN_MIN_PX`. On a 1728 laptop that floor leaves room for 708px of panel and the
 * flat cap stops it at 560; on a 2560 monitor it leaves 1540.
 *
 * So the ceiling is whichever is LARGER, which makes this purely additive: never
 * narrower than before, wider only where the floor says there is room. Below about
 * 1830 the flat cap is still the binding one, so nothing changes on the windows
 * where the rail-fold behaviour was tuned.
 */
export function maxChatWidthFor(viewport: number): number {
  const roomToTheFloor =
    viewport -
    LAYOUT.NAV_COLLAPSED_PX -
    LAYOUT.GUTTER_PX * 2 -
    LAYOUT.PANEL_INSET_PX -
    LAYOUT.MAIN_MIN_PX;
  // The larger of the two, so a big monitor is not held to a number chosen for a
  // laptop and a laptop still gets its full historic travel.
  //
  // Capping this at the room was tried twice and is the wrong lever. It leaves a
  // laptop with a ceiling at or below the width it already has, so the handle does
  // nothing at all, which is a worse answer than a squeezed page. Widening past the
  // content floor no longer folds anything: `resolveLayoutFit` asks about
  // CHAT_MIN_PX, so the fold is a fact about the window and not about this number.
  return Math.max(LAYOUT.CHAT_MAX_PX, roomToTheFloor);
}

/**
 * Clamp a dragged panel width to what the panel supports.
 *
 * `viewport` is optional so a caller with no window (the store's first read, tests)
 * keeps the flat cap rather than guessing a size.
 */
export function clampChatWidth(width: number, viewport?: number): number {
  const ceiling =
    viewport === undefined ? LAYOUT.CHAT_MAX_PX : maxChatWidthFor(viewport);
  return Math.min(ceiling, Math.max(LAYOUT.CHAT_MIN_PX, width));
}
